<script setup lang="ts">
/**
 * Section « texte + visuel » des pages charte « apple » (Karim 2026-10-18) :
 * surtitre bleu facultatif, titre fort, texte, points cochés, puis un visuel
 * (slot `media` : téléphone, capture, photo) à gauche ou à droite. Le slot
 * par défaut accueille un complément sous les points (lien, bouton, note).
 */
import { TITRE_SECTION_APPLE } from "~/utils/titresApple";

/** Un point : une phrase, ou un titre en gras + une explication. */
export type PuceApple = string | { titre: string; texte?: string; icone?: string };

const props = withDefaults(
  defineProps<{
    surtitre?: string;
    titre: string;
    description?: string;
    puces?: PuceApple[];
    /** Côté du visuel sur grand écran. */
    media?: "gauche" | "droite";
    fond?: "blanc" | "gris" | "noir";
  }>(),
  { surtitre: undefined, description: undefined, puces: () => [], media: "droite", fond: "blanc" },
);

const sombre = computed(() => props.fond === "noir");
</script>

<template>
  <section
    class="px-4 py-20 sm:px-8 lg:py-28"
    :class="{ 'bg-white': props.fond === 'blanc', 'bg-[#f5f5f7]': props.fond === 'gris', 'bg-black': sombre }"
  >
    <div class="mx-auto grid max-w-[1120px] items-center gap-12 lg:grid-cols-2 lg:gap-16">
      <div :class="props.media === 'gauche' && 'lg:order-2'">
        <p v-if="props.surtitre" class="pa-reveal mb-3 text-[17px] font-bold" :class="sombre ? 'text-[#2997ff]' : 'text-(--color-main)'">
          {{ props.surtitre }}
        </p>
        <h2
          class="pa-reveal mb-5 text-[32px] [text-wrap:balance] sm:text-[44px]"
          :class="sombre ? 'text-white' : 'text-[#1d1d1f]'"
          :style="TITRE_SECTION_APPLE"
        >
          {{ props.titre }}
        </h2>
        <div
          v-if="props.description || $slots.description"
          class="texte-riche pa-reveal mb-8 text-[17px] font-medium leading-[1.55]"
          :class="sombre ? 'text-white/70' : 'text-[#424245]'"
        >
          <!-- slot `description` : texte riche (lien, mot en gras) -->
          <slot name="description"><p>{{ props.description }}</p></slot>
        </div>
        <ul v-if="props.puces.length" class="pa-reveal mb-8 space-y-4" style="--d: 100ms">
          <li
            v-for="(puce, i) in props.puces"
            :key="i"
            class="flex gap-3 text-[16px] leading-[1.5]"
            :class="sombre ? 'text-white/85' : 'text-[#1d1d1f]'"
          >
            <UIcon
              :name="(typeof puce === 'object' && puce.icone) || 'i-lucide-check-circle-2'"
              class="mt-0.5 size-5 shrink-0"
              :class="sombre ? 'text-[#2997ff]' : 'text-(--color-main)'"
              aria-hidden="true"
            />
            <span v-if="typeof puce === 'string'">{{ puce }}</span>
            <span v-else>
              <span class="block font-semibold" :class="sombre ? 'text-white' : 'text-[#1d1d1f]'">{{ puce.titre }}</span>
              <span v-if="puce.texte" class="block text-[15px]" :class="sombre ? 'text-white/60' : 'text-[#6e6e73]'">{{ puce.texte }}</span>
            </span>
          </li>
        </ul>
        <div v-if="$slots.default" class="pa-reveal">
          <slot />
        </div>
      </div>
      <div class="pa-reveal" :class="props.media === 'gauche' && 'lg:order-1'" style="--d: 120ms">
        <slot name="media" />
      </div>
    </div>
  </section>
</template>

<style scoped>
.texte-riche :deep(a) {
  font-weight: 600;
  color: var(--color-main);
}
.texte-riche :deep(a:hover) {
  text-decoration: underline;
}
</style>
